import { ERR_INVALID_ARGUMENT } from "../error";
import { hash as arraybufferHash } from "../arraybuffer";
/**
 * Returns true if the given object is a string. No type conversion will be performed.
 */
export function isString(pObj) {
    return (typeof pObj === "string" || pObj instanceof String);
}
/**
 * Returns true if the string is null or empty.
 */
export function isNullOrEmpty(pString) {
    return (pString == null || pString === "");
}
/**
 * Returns true if the string is NOT null or empty.
 */
export function isNotNullOrEmpty(pString) {
    return (pString != null && pString !== "");
}
/**
 * Removes pChar (default: " ") from the left and right end of the string.
 */
export function trim(pString, pChar = " ") {
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tChar = regExpEscape(pChar);
    return (pString.replace(new RegExp("^" + tChar + "+|" + tChar + "+$", "g"), ""));
}
/**
 * Removes pChar (default: " ") from the left end of the string.
 */
export function trimLeft(pString, pChar = " ") {
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tChar = regExpEscape(pChar);
    return (pString.replace(new RegExp("^" + tChar + "+"), ""));
}
/**
 * Removes pChar (default: " ") from the right end of the string.
 */
export function trimRight(pString, pChar = " ") {
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tChar = regExpEscape(pChar);
    return (pString.replace(new RegExp(tChar + "+$"), ""));
}
/**
 * Compares two strings.
 */
export function compare(pString1, pString2) {
    return (pString1 < pString2 ? -1 : pString1 > pString2 ? 1 : 0);
}
/**
 * Returns the file extension of the given url. The returned extension will be always lowercase.
 * www.example.com/test/123.jpg ==> .jpg
 */
export function getFileExtension(pUrl) {
    if (pUrl == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tCleanedUrl = removeQueryString(pUrl);
    const tIndex = (tCleanedUrl.lastIndexOf("."));
    if (tIndex < 0)
        return ("");
    const tExt = tCleanedUrl.slice(tIndex).toLowerCase();
    //simple validation, extensions do not contain a slash
    if (tExt.includes("/") || tExt.includes("%2f"))
        return ("");
    return (tExt);
}
/**
 * Returns the path of the given url.
 * www.example.com/test/123.jpg ==> www.example.com/test/
 */
export function getFilePath(pUrl) {
    if (pUrl == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tCleanedUrl = removeQueryString(pUrl);
    const tIndex = (tCleanedUrl.lastIndexOf("/"));
    if (tIndex < 0)
        return ("");
    return (tCleanedUrl.slice(0, tIndex + 1));
}
/**
 * Returns the filename of the given url.
 * www.example.com/test/123.jpg ==> 123.jpg
 */
export function getFileName(pUrl) {
    if (pUrl == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tCleanedUrl = removeQueryString(pUrl);
    const tIndex = (tCleanedUrl.lastIndexOf("/"));
    if (tIndex < 0)
        return ("");
    return (tCleanedUrl.slice(tIndex + 1));
}
/**
 * Joins given path segements into one path.
 * Resolving of ".." or "." is not supported.
 */
export function joinPath(...pSegments) {
    if (pSegments.length === 0)
        return ("");
    const tCleanedSegments = new Array();
    for (const tRawSegment of pSegments) {
        let tSegment = tRawSegment;
        if (tSegment.startsWith("/"))
            tSegment = tSegment.slice(1);
        if (tSegment.endsWith("/"))
            tSegment = tSegment.slice(0, -1);
        if (tSegment.length > 0) {
            tCleanedSegments.push(tSegment);
        }
    }
    //preserve leading and trailing slash
    let tPath = tCleanedSegments.join("/");
    if (pSegments[0].startsWith("/"))
        tPath = "/" + tPath;
    if (pSegments[pSegments.length - 1].endsWith("/"))
        tPath += "/";
    return (tPath);
}
/**
 * Returns the value of the given parameter.
 * www.example.com/test/123.jpg?x=1&y=2 ==> 2 (for pParam = "y")
 */
export function getUrlParamValue(pUrl, pParam) {
    if (pUrl == null || pParam == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tRegEx = new RegExp('[?|&]' + pParam + '=' + '([^&;]+?)(&|#|;|$)');
    const tResult = tRegEx.exec(pUrl);
    if (tResult != null && tResult.length > 1) {
        const tValue = tResult[1].replace(/\+/g, '%20');
        return (decodeURIComponent(tValue));
    }
    return (null);
}
/**
 * Removes the query string from the given url.
 * www.example.com/test/?x=1&y=2 ==> www.example.com/test/
 */
export function removeQueryString(pUrl) {
    const tIndex = pUrl.indexOf("?");
    if (tIndex >= 0) {
        return (pUrl.substring(0, tIndex));
    }
    else {
        return (pUrl);
    }
}
/**
 * Converts a Blob into a string.
 * If pAsDataUri is true the returned string is a data-uri, otherwise its returned as text.
 */
export function fromBlob(pBlob, pAsDataUri = false) {
    if (pBlob == null)
        return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
    const tPromise = new Promise((pResolve, pReject) => {
        const tFileReader = new FileReader();
        tFileReader.onload = () => {
            pResolve(tFileReader.result);
        };
        tFileReader.onerror = () => {
            if (tFileReader.error != null)
                pReject(new Error("Failed to read from blob: " + tFileReader.error.name));
            else
                pReject(new Error("Failed to read from blob."));
        };
        if (pAsDataUri)
            tFileReader.readAsDataURL(pBlob);
        else
            tFileReader.readAsText(pBlob);
    });
    return (tPromise);
}
export function isDataUri(pUri) {
    if (isNullOrEmpty(pUri))
        return (false);
    return (pUri.length < 5 ? false : pUri.substr(0, 5) === "data:");
}
export function htmlEscape(pString) {
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    return (pString
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\//g, '&#x2F;'));
}
export function htmlUnescape(pString) {
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    return (pString
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&#x2F;/g, '/'));
}
export function regExpEscape(pString) {
    if (pString == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    //eslint-disable-next-line no-useless-escape
    return (pString.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
}
/** Returns a randomly generated UUID */
export function generateRandomUUID() {
    //based on https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */
    const tCryptoObj = window.crypto || window.msCrypto; //IE11 support
    return ("" + 1e7 + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (c ^ tCryptoObj.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
}
/**
 * Computes the hash for the given string and returns it as hex string.
 * Note: This function requires a secure connection (HTTPS).
 */
export async function hash(pString, pAlgorithm) {
    const tUtf8 = encodeToUtf8(pString); // encode to utf-8 to handle unicode chars
    return (arraybufferHash(tUtf8, pAlgorithm));
}
/** Encodes the given UTF-16 string to UTF-8 */
export function encodeToUtf8(pString) {
    return (new TextEncoder().encode(pString));
}
